/**
 * Espace Interventions (liste par onglet, planification d'une intervention sans
 * date), fiche intervention et ses pages : pièce, signature, clôture,
 * réouverture, modification, retour, à prévoir. Les archives sont l'onglet
 * « Archivées » ; leur ancienne adresse y mène. Chaque page de saisie porte le
 * droit qui l'ouvre ; le serveur vérifie en plus l'affectation.
 */
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { AppRoute } from '../../app/route-types.js';

const InterventionsPage = lazy(() => import('./InterventionsPage.js').then((m) => ({ default: m.InterventionsPage })));
const ScheduleInterventionPage = lazy(() => import('./list/ScheduleInterventionPage.js').then((m) => ({ default: m.ScheduleInterventionPage })));
const InterventionDetail = lazy(() => import('./InterventionDetail.js').then((m) => ({ default: m.InterventionDetail })));
const InterventionEditPage = lazy(() => import('./InterventionPages.js').then((m) => ({ default: m.InterventionEditPage })));
const ReturnVisitPage = lazy(() => import('./InterventionPages.js').then((m) => ({ default: m.ReturnVisitPage })));
const NeedNewPage = lazy(() => import('./FieldNeeds.js').then((m) => ({ default: m.NeedNewPage })));
const NoteEditPage = lazy(() => import('./NoteEditPage.js').then((m) => ({ default: m.NoteEditPage })));
const PartNewPage = lazy(() => import('./record/PartNewPage.js').then((m) => ({ default: m.PartNewPage })));
const SignaturePage = lazy(() => import('./record/SignaturePage.js').then((m) => ({ default: m.SignaturePage })));
const ClosurePage = lazy(() => import('./record/ClosurePage.js').then((m) => ({ default: m.ClosurePage })));
const ReopenPage = lazy(() => import('./record/ReopenPage.js').then((m) => ({ default: m.ReopenPage })));

export const interventionsRoutes: AppRoute[] = [
  // Ouverte à tous : le serveur ne montre à chacun que ce que le planning lui laisse voir.
  { path: '/interventions', element: <InterventionsPage /> },
  { path: '/interventions/archives', element: <Navigate to="/interventions?onglet=archivees" replace /> },
  { path: '/interventions/:id', element: <InterventionDetail /> },
  {
    path: '/interventions/:id/modifier',
    element: <InterventionEditPage />,
    gate: { anyOf: ['planning.edit_all', 'planning.edit_own'] },
  },
  {
    path: '/interventions/:id/planifier',
    element: <ScheduleInterventionPage />,
    gate: { anyOf: ['planning.edit_all', 'planning.edit_own'] },
  },
  {
    path: '/interventions/:id/retour',
    element: <ReturnVisitPage />,
    gate: { anyOf: ['intervention.create_any', 'intervention.create_own'] },
  },
  { path: '/interventions/:id/a-prevoir/nouveau', element: <NeedNewPage /> },
  // Le carnet : l'auteur corrige sa note (le serveur vérifie l'auteur et l'affectation).
  { path: '/interventions/:id/carnet/:noteId/corriger', element: <NoteEditPage /> },
  // Le rapport : le technicien affecté et le bureau (vérifié par le serveur, `reports/access.ts`).
  { path: '/interventions/:id/pieces/nouvelle', element: <PartNewPage /> },
  { path: '/interventions/:id/signature', element: <SignaturePage /> },
  {
    path: '/interventions/:id/cloture',
    element: <ClosurePage />,
    gate: { anyOf: ['intervention.close_any', 'intervention.close_assigned'] },
  },
  {
    path: '/interventions/:id/reouverture',
    element: <ReopenPage />,
    gate: { anyOf: ['intervention.reopen'] },
  },
];
